import React from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { resolveImage } from './CatalogHelpers';

const PAGE_W = 1000;                    // Ancho fijo del lienzo
const GRID_GAP = 12;
const GRID_PADDING = 18;

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const MESES_ES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
function formatEndDate(iso) {
  if (!iso) return null;
  // iso: "2026-08-15" → "15 ago"
  const parts = String(iso).split('-');
  if (parts.length < 3) return null;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (!m || !d) return null;
  return `${String(d).padStart(2, '0')} ${MESES_ES[m - 1]}`;
}

// Grid adaptativo: menos columnas cuando hay pocos items, más cuando hay muchos
function layoutFor(count) {
  if (count <= 2) return { cols: count, imgH: 320, cardPad: 14, titleSize: 16, priceSize: 26 };
  if (count <= 4) return { cols: 2, imgH: 280, cardPad: 12, titleSize: 15, priceSize: 24 };
  if (count <= 9) return { cols: 3, imgH: 220, cardPad: 10, titleSize: 14, priceSize: 22 };
  if (count <= 16) return { cols: 4, imgH: 180, cardPad: 9, titleSize: 13, priceSize: 20 };
  if (count <= 25) return { cols: 5, imgH: 150, cardPad: 8, titleSize: 12, priceSize: 18 };
  return { cols: 5, imgH: 130, cardPad: 7, titleSize: 11, priceSize: 16 };
}

function PrintableCatalog({ promos, generatedAt, seller }) {
  const L = layoutFor(promos.length);

  return (
    <div style={{
      width: PAGE_W,
      background: '#ffffff',
      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      color: '#0f172a',
      display: 'flex', flexDirection: 'column',
      boxSizing: 'border-box',
    }}>
      {/* HEADER */}
      <div style={{
        background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
        color: 'white',
        padding: '20px 28px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: 2, opacity: 0.85, textTransform: 'uppercase', fontWeight: 600 }}>
            Ofertas Vigentes
          </div>
          <div style={{ fontSize: 30, fontWeight: 900, marginTop: 2, letterSpacing: -0.5 }}>
            Promociones
          </div>
          <div style={{ fontSize: 11, opacity: 0.9, marginTop: 4, fontStyle: 'italic' }}>
            Ofertas y precios con cambios sin previo aviso
          </div>
        </div>
        <div style={{ textAlign: 'right', fontSize: 11, opacity: 0.9 }}>
          <div>Generado</div>
          <div style={{ fontWeight: 700, fontSize: 13 }}>{generatedAt}</div>
          <div style={{
            marginTop: 6,
            fontWeight: 800, fontSize: 13, lineHeight: 1.4,
            color: '#fef3c7',
          }}>
            {`${promos.length} oferta${promos.length === 1 ? '' : 's'}`}
          </div>
        </div>
      </div>

      {/* GRID */}
      <div style={{
        padding: GRID_PADDING,
        background: '#fef2f2',
        display: 'grid',
        gridTemplateColumns: `repeat(${L.cols}, 1fr)`,
        gap: GRID_GAP,
        alignContent: 'start',
      }}>
        {promos.map((p) => {
          const hasBase = p.base_price > p.promo_price && p.promo_price > 0;
          const discount = hasBase ? Math.round((1 - p.promo_price / p.base_price) * 100) : 0;
          const scopeLbl = p.scope === 'local' ? 'Solo tienda' : p.scope === 'shipment' ? 'Solo envío' : 'Tienda y envío';
          const scopeColor = p.scope === 'local' ? '#7c3aed' : p.scope === 'shipment' ? '#2563eb' : '#059669';
          const isNewOnly = p.is_new_arrival && !p.has_promo;
          const displayPrice = isNewOnly ? p.base_price : p.promo_price;
          return (
            <div key={`${p.kind}-${p.id}`} style={{
              border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden',
              background: '#ffffff', boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
              display: 'flex', flexDirection: 'column',
            }}>
              {/* Imagen - PROPORCIONAL (contain) con fondo blanco */}
              <div style={{
                position: 'relative',
                width: '100%',
                height: L.imgH,
                background: '#ffffff',
                overflow: 'hidden',
                borderBottom: '1px solid #f1f5f9',
              }}>
                {p.image_url ? (
                  <img
                    src={resolveImage(p.image_url)}
                    alt={p.name}
                    crossOrigin="anonymous"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                ) : (
                  <div style={{
                    width: '100%', height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 40, color: '#cbd5e1',
                  }}>—</div>
                )}
                {discount > 0 && (
                  <div style={{
                    position: 'absolute', top: 6, left: 6,
                    color: '#dc2626',
                    fontSize: L.cols >= 4 ? 15 : 18, fontWeight: 900,
                    lineHeight: 1.2,
                    textShadow: '0 1px 2px rgba(255,255,255,0.9), 0 -1px 0 #fff, 1px 0 0 #fff, -1px 0 0 #fff',
                  }}>-{discount}%</div>
                )}
                {p.is_new_arrival && (
                  <div style={{
                    position: 'absolute',
                    top: discount > 0 ? 28 : 6,
                    left: 6,
                    color: '#059669',
                    fontSize: L.cols >= 4 ? 12 : 14, fontWeight: 900,
                    lineHeight: 1.2,
                    letterSpacing: 0.8,
                    textShadow: '0 1px 2px rgba(255,255,255,0.9), 0 -1px 0 #fff, 1px 0 0 #fff, -1px 0 0 #fff',
                  }}>NUEVO</div>
                )}
                {p.kind === 'package' && (
                  <div style={{
                    position: 'absolute', top: 6, right: 6,
                    color: '#7c3aed',
                    fontSize: 11, fontWeight: 900,
                    lineHeight: 1.2,
                    letterSpacing: 0.5,
                    textShadow: '0 1px 2px rgba(255,255,255,0.9), 0 -1px 0 #fff, 1px 0 0 #fff, -1px 0 0 #fff',
                  }}>PAQUETE</div>
                )}
              </div>

              {/* Info */}
              <div style={{
                padding: L.cardPad, display: 'flex', flexDirection: 'column',
                gap: 6,
              }}>
                <div style={{
                  fontSize: L.titleSize, fontWeight: 700, lineHeight: 1.25,
                  color: '#0f172a',
                  wordBreak: 'break-word',
                }}>
                  {p.name}
                </div>

                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: L.priceSize, fontWeight: 900, color: isNewOnly ? '#059669' : '#b91c1c', lineHeight: 1,
                  }}>{money(displayPrice)}</span>
                  {hasBase && !isNewOnly && (
                    <span style={{
                      fontSize: Math.max(11, L.titleSize - 2),
                      color: '#94a3b8', textDecoration: 'line-through',
                    }}>{money(p.base_price)}</span>
                  )}
                </div>

                {p.promo_type === 'min_qty' && p.promo_min_qty > 0 && (
                  <div style={{
                    fontSize: 12,
                    color: '#1d4ed8',
                    fontWeight: 800,
                    lineHeight: 1.4,
                    alignSelf: 'flex-start',
                  }}>
                    Comprando {p.promo_min_qty}+
                  </div>
                )}

                <div style={{
                  display: 'flex', gap: 10, flexWrap: 'wrap',
                  paddingTop: 2,
                  fontSize: 12, fontWeight: 800, lineHeight: 1.4,
                }}>
                  <span style={{ color: scopeColor }}>{scopeLbl}</span>
                  {(() => {
                    const endStr = formatEndDate(p.promo_end_date_iso);
                    if (!endStr) return null;
                    const dl = p.days_left;
                    const c = dl == null ? '#059669'
                      : dl === 0 ? '#dc2626'
                      : dl <= 3 ? '#ea580c'
                      : dl <= 7 ? '#d97706'
                      : '#059669';
                    const label = dl === 0 ? '¡Termina hoy!' : `Vence ${endStr}`;
                    return (
                      <span style={{ color: c }}>
                        {label}
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* FOOTER VENDEDOR */}
      <div style={{
        padding: '22px 28px 26px',
        borderTop: '4px solid #dc2626',
        background: seller
          ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)'
          : '#f8fafc',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, boxSizing: 'border-box',
      }}>
        {seller ? (
          <>
            <div style={{ lineHeight: 1.3 }}>
              <div style={{
                fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.5,
                color: '#065f46', fontWeight: 700,
              }}>
                Atendido por
              </div>
              <div style={{
                fontSize: 22, fontWeight: 900, color: '#0f172a', marginTop: 2,
              }}>
                {seller.name}
              </div>
              {seller.phone && (
                <div style={{
                  fontSize: 18, fontWeight: 900, color: '#059669', marginTop: 4,
                  lineHeight: 1.4,
                }}>
                  WhatsApp: {seller.phone}
                </div>
              )}
            </div>
            <div style={{
              textAlign: 'right', fontSize: 12, color: '#065f46',
              maxWidth: 260, fontWeight: 600,
            }}>
              Consulta disponibilidad y realiza tu pedido directamente conmigo
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 13, color: '#334155' }}>
              <div style={{ fontWeight: 800, fontSize: 16, color: '#0f172a' }}>
                Sistema POS
              </div>
              <div>Consulta disponibilidad con tu vendedor</div>
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>
              Ofertas actualizadas al {generatedAt}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Pre-carga imágenes vía fetch → dataURL (asegura CORS y evita imágenes rotas en canvas)
async function preloadImagesAsDataURL(promos) {
  const cache = new Map();
  const out = await Promise.all(promos.map(async (p) => {
    if (!p.image_url) return p;
    const src = resolveImage(p.image_url);
    if (cache.has(src)) return { ...p, image_url: cache.get(src) };
    try {
      const res = await fetch(src, { mode: 'cors' });
      if (!res.ok) throw new Error('img fail');
      const blob = await res.blob();
      const dataUrl = await new Promise((resolve) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = () => resolve('');
        r.readAsDataURL(blob);
      });
      cache.set(src, dataUrl);
      return { ...p, image_url: dataUrl };
    } catch {
      return { ...p, image_url: '' };
    }
  }));
  return out;
}

export async function generateOffersFile({ promos, format = 'pdf', seller = null, onProgress = () => {} }) {
  if (!promos || promos.length === 0) throw new Error('No hay ofertas para exportar');

  const now = new Date();
  const mesesEs = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  const generatedAt = `${String(now.getDate()).padStart(2, '0')} ${mesesEs[now.getMonth()]} ${now.getFullYear()} · ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  onProgress({ page: 1, total: 1 });

  // Pre-cargar imágenes a dataURL para máxima compatibilidad en el canvas
  const promosReady = await preloadImagesAsDataURL(promos);

  // Renderizar fuera de pantalla
  const host = document.createElement('div');
  host.style.cssText = 'position:fixed;left:-99999px;top:0;pointer-events:none;background:#fff;';
  document.body.appendChild(host);

  const { createRoot } = await import('react-dom/client');
  let canvas;
  try {
    const el = document.createElement('div');
    host.appendChild(el);
    const root = createRoot(el);
    root.render(<PrintableCatalog promos={promosReady} generatedAt={generatedAt} seller={seller} />);

    // Esperar a que React monte y las imágenes carguen
    await new Promise((r) => setTimeout(r, 400));
    const imgs = el.querySelectorAll('img');
    await Promise.all(Array.from(imgs).map((img) =>
      img.complete && img.naturalWidth > 0
        ? Promise.resolve()
        : new Promise((res) => {
            img.onload = () => res();
            img.onerror = () => res();
            setTimeout(() => res(), 3000);
          })
    ));
    await new Promise((r) => setTimeout(r, 200));

    canvas = await html2canvas(el.firstChild, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      allowTaint: false,
      logging: false,
      windowWidth: PAGE_W,
    });
    root.unmount();
  } finally {
    host.remove();
  }

  const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;

  if (format === 'pdf') {
    // PDF de UNA sola página con el tamaño exacto del canvas (sin estirar)
    const pxW = canvas.width;
    const pxH = canvas.height;
    // Convertir a puntos manteniendo proporción: usar 595pt de ancho (A4)
    const pdfW = 595;
    const pdfH = pdfW * (pxH / pxW);
    const pdf = new jsPDF({
      orientation: pdfH > pdfW ? 'portrait' : 'landscape',
      unit: 'pt',
      format: [pdfW, pdfH],
      compress: true,
    });
    const imgData = canvas.toDataURL('image/jpeg', 0.9);
    pdf.addImage(imgData, 'JPEG', 0, 0, pdfW, pdfH, undefined, 'FAST');
    pdf.save(`ofertas-${stamp}.pdf`);
    return { pages: 1 };
  }

  // Imagen PNG única (una sola imagen continua)
  await new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) { resolve(); return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ofertas-${stamp}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => { URL.revokeObjectURL(url); resolve(); }, 500);
    }, 'image/png');
  });
  return { pages: 1 };
}
